import React from 'react'
import {
	teacher_selfStartPath,
	teacher_studentStartPath
} from '../components/OnboardingHomepage/EducatorHomepage/data'
import {
	convertTeacherStartTrack,
	convertStudentStartTrack,
	fetchOnboardingData,
	status,
	handleSlackOnboarding
} from '../components/OnboardingHomepage/onboardingUtils'
import _ from 'lodash'

/*
    This context is responsible for storing data relevant to onboarding guidance in
    the frontend, and is also responsible for keeping track of which step is expaned/
    active in the Homepage.
    
    List of data that is being tracked:
     -Teacher self onboarding path/ Teacher student onboarding path
        1. total steps
        2. how many has been completed
        3. status of each step
     -Is all of the onboarding steps completed? (use for onboarding banner)
     -activeKeyList (use for dropdown list in Onboarding Homepage)
*/
export const OnboardingTeacherContext = React.createContext()

const OnboardingTeacherContextProvider = ({ current_user, children }) => {
	//if the current index is 'in-progress' then it should be expanded by default, initialized to have the first item to be expaned by default
	const [activeKeyList, setActiveKeyList] = React.useState(
		Array.from(
			{ length: teacher_selfStartPath.length + teacher_studentStartPath.length },
			(val, index) => {
				return index === 0 || index === teacher_selfStartPath.length ? true : false
			}
		)
	)

	//initializing onboarding paths
	let teacherStartTrackInit = {
		totalSteps: _.size(teacher_selfStartPath),
		completedSteps: 0,
		isPersonalOnboardingCompleted: false,
		steps: {
			exploreContent: _.indexOf(status, 'inProgress'),
			assignYourself: _.indexOf(status, 'disabled'),
			getInGame: _.indexOf(status, 'disabled'),
			completeQuest: _.indexOf(status, 'disabled')
		}
	}
	let studentStartTrackInit = {
		totalSteps: _.size(teacher_studentStartPath),
		completedSteps: 0,
		isClassOnboardingCompleted: false,
		steps: {
			createClass: _.indexOf(status, 'inProgress'),
			addStudent: _.indexOf(status, 'disabled'),
			studentInGame: _.indexOf(status, 'disabled'),
			assignToStudent: _.indexOf(status, 'disabled'),
			studentCompleteQuest: _.indexOf(status, 'disabled')
		}
	}

	//onboarding states to be tracked
	const [teacherStartTrack, setTeacherStartTrack] = React.useState(teacherStartTrackInit)
	const [studentStartTrack, setStudentStartTrack] = React.useState(studentStartTrackInit)
	const [isAllComplete, setIsAllComplete] = React.useState(!current_user['is-educator'])
	const [currentPath, setCurrentPath] = React.useState('teacher_selfStartPath')

	const onboardingTeacherContextValue = React.useMemo(
		() => ({
			activeKeyList,
			setActiveKeyList,
			teacherStartTrack,
			setTeacherStartTrack,
			studentStartTrack,
			setStudentStartTrack,
			isAllComplete,
			setIsAllComplete,
			currentPath,
			setCurrentPath,
			current_user
		}),
		[
			activeKeyList,
			teacherStartTrack,
			studentStartTrack,
			isAllComplete,
			currentPath,
			current_user
		]
	)

	//handles setting initial/refetched onboarding data with responsed returned from backend
	const handleInitializeOnboardingContext = (data, sendSlackNotification) => {
		const teacherTrackData = convertTeacherStartTrack(data)
		const studentTrackData = convertStudentStartTrack(data)
		const completedSteps = data.allCompletedSteps.reduce(
			(prev, curr) => {
				return {
					teacherCompletedStep:
						curr <= 3 ? prev.teacherCompletedStep + 1 : prev.teacherCompletedStep,
					studentCompletedStep:
						curr > 3 ? prev.studentCompletedStep + 1 : prev.studentCompletedStep
				}
			},
			{ teacherCompletedStep: 0, studentCompletedStep: 0 }
		)

		const hasAllCompleted = data.allOnboardingStepsCompleted
		setIsAllComplete(hasAllCompleted)

		/*  handle sending query to request for slack notification:
            slack notification should only be send ONCE for each of the tracks,
            thus, the implementation should track && compare the previous status
            of track completion with the incoming status for track completion, AND
            only if previous status = false && incoming status = true, should slack
            notification be send. ONLY after the slack notification is queried, should 
            the context be updated.
        */

		setTeacherStartTrack(prev => {
			if (
				sendSlackNotification &&
				data.personalOnboardingStepsCompleted &&
				!prev.isPersonalOnboardingCompleted
			) {
				handleSlackOnboarding(
					current_user,
					`${
						data.classOnboardingStepsCompleted
							? 'both sequences'
							: 'Self-Assign Sequence'
					}`
				)
			}

			return {
				...prev,
				...teacherTrackData,
				completedSteps: completedSteps.teacherCompletedStep
			}
		})

		setStudentStartTrack(prev => {
			if (
				sendSlackNotification &&
				data.classOnboardingStepsCompleted &&
				!prev.isClassOnboardingCompleted
			) {
				handleSlackOnboarding(
					current_user,
					`${
						data.personalOnboardingStepsCompleted
							? 'both sequences'
							: 'Student Sequence'
					}`
				)
			}
			return {
				...prev,
				...studentTrackData,
				completedSteps: completedSteps.studentCompletedStep
			}
		})

		return { teacherTrackData, studentTrackData, hasAllCompleted, completedSteps }
	}

	React.useEffect(() => {
		//callback for initial fetch of the onboarding page
		const fetchOnboardingInit = {
			onComplete: json => {
				const errors = json.data.instructorOnboardingContext.error

				if (!_.isEmpty(errors)) {
					console.error(errors.map(error => error.message))
				} else {
					const { teacherTrackData, studentTrackData, hasAllCompleted, completedSteps } =
						handleInitializeOnboardingContext(
							json.data.instructorOnboardingContext,
							false
						)
					//if onboarding process is completed for current user, stop calling fetch function every 5 sec
					if (hasAllCompleted) {
						clearInterval(refetchOnboarding)
					}

					//on initial load of the page, only the inprogress items should be expanded by default
					setActiveKeyList([
						..._.values(teacherTrackData.steps).map(
							value => value === _.indexOf(status, 'inProgress')
						),
						..._.values(studentTrackData.steps).map(
							value => value === _.indexOf(status, 'inProgress')
						)
					])

					setCurrentPath(prev => {
						if (teacherTrackData.isPersonalOnboardingCompleted) {
							return 'teacher_studentStartPath'
						} else if (
							completedSteps.teacherCompletedStep <=
								completedSteps.studentCompletedStep ||
							studentTrackData.isClassOnboardingCompleted
						) {
							return 'teacher_selfStartPath'
						} else {
							return 'teacher_studentStartPath'
						}
					})
				}
			},
			onFailure: error => {
				console.error(error)
			}
		}

		//callback for each refetch of the onboarding page
		const fetchOnboardingUpdate = {
			onComplete: json => {
				const errors = json.data.instructorOnboardingContext.error

				if (!_.isEmpty(errors)) {
					console.error(errors.map(error => error.message))
				} else {
					const { teacherTrackData, studentTrackData, hasAllCompleted } =
						handleInitializeOnboardingContext(
							json.data.instructorOnboardingContext,
							true
						)

					//if onboarding process is completed for current user, stop calling fetch function every 5 sec
					if (hasAllCompleted) {
						clearInterval(refetchOnboarding)
					}

					//below is to make sure that list items that are clicked open between refreshes are remembered and stay expaned, otherwise they would default to collapse on each refetch
					setActiveKeyList(prev => {
						return [
							..._.values(teacherTrackData.steps).map((value, index) =>
								value === _.indexOf(status, 'inProgress') ? true : prev[index]
							),
							..._.values(studentTrackData.steps).map((value, index) =>
								value === _.indexOf(status, 'inProgress')
									? true
									: prev[_.size(teacher_selfStartPath) + index]
							)
						]
					})
					//set current path to the incomplete path && do nothing if neither is completed
					if (
						teacherTrackData.isPersonalOnboardingCompleted &&
						!studentTrackData.isClassOnboardingCompleted
					) {
						setCurrentPath('teacher_studentStartPath')
					} else if (
						!teacherTrackData.isPersonalOnboardingCompleted &&
						studentTrackData.isClassOnboardingCompleted
					) {
						setCurrentPath('teacher_selfStartPath')
					}
				}
			},
			onFailure: error => {
				console.log(error)
			}
		}

		let refetchOnboarding
		// inital call on page load
		if (current_user['is-educator']) {
			fetchOnboardingData(fetchOnboardingInit)
			/*subsequent calls every 5 sec -> this should be updated later down the line such that we don't have 
            to refetch from the backend every 5 sec. By doing refetch, the tasks that are inprogress are 'forced' 
            to be expanded by default, as the onboarding page needed to update itself with data returned from the 
            backend, however this means that the inprogress items will always be updated to be expanded. 
            */
			refetchOnboarding = setInterval(() => {
				fetchOnboardingData(fetchOnboardingUpdate)
			}, 5000)
		}
	}, [current_user])

	return (
		<OnboardingTeacherContext.Provider value={onboardingTeacherContextValue}>
			{children}
		</OnboardingTeacherContext.Provider>
	)
}

export default OnboardingTeacherContextProvider
